/**
 * PRP-WEB-2 W2.1/W2.2: the web plan's drag controller. A port of the phone's
 * `SketchGestureController.dragStart/Move/End` (frontend/components/sketch/controller/gestures.ts),
 * in plan feet (the web's view box needs no screen transform). Pure TS, no React: the page
 * feeds it plan points and gets back the working plan to draw and, on release, the plan to commit.
 *
 * What a press grabs (`grabAt`): a door or window slides along its wall; a wall drags along its
 * normal (a shared wall moves its neighbour, `dragSharedWall`; a split line is a 0 in missing wall
 * and drags the same way); a room moves and snaps flush (`dragRoom` / `snapRoom`); Ctrl on a wall
 * pulls out a new room (`dragOutPreview`). Alt held = no snapping (DD4). Every drag is one undo
 * step: the page checkpoints `before` once, on release.
 */
import {
  beginOpeningDrag, beginRoomDrag, beginWallDrag, bbox, clone, dist, dragOpening, dragOutPreview, dragRoom,
  dragSegmentPreview, dragSharedWall, dragWall, edgeOf, findRoom, finishOpeningDrag, finishRoomDrag, finishWallDrag,
  hitTest, missingAsWall, outN, droppedText, resolveOpening, sharedMap, snapIn, wallFtOf, type DragFinish,
  type Hit, type OpeningDrag, type Plan, type Pt, type RoomDrag, type WallDrag,
} from '@sketch-core';
import { fmtFt } from '../PlanCanvas';

export type Sel =
  | { type: 'room'; rid: string }
  | { type: 'wall'; rid: string; vid: number; off: number }
  | { type: 'open'; rid: string; oid: number };

/** A press, in plan feet, with the screen's scale. */
export interface PressInfo {
  /** Hit tolerance in plan feet (a few screen pixels at the current zoom). */
  tolFt: number;
  /** Screen pixels per plan foot (room snapping reach). */
  pxPerFt: number;
  ctrl?: boolean;
  alt?: boolean;
  /** Touch: only the selected item drags; anything else pans (the phone's rule). */
  touch?: boolean;
}

export type Grab =
  | { kind: 'room'; rid: string }
  | { kind: 'wall'; rid: string; vid: number; off: number }
  | { kind: 'open'; rid: string; oid: number }
  | { kind: 'dragout'; rid: string; vid: number };

/** What the canvas draws while a drag is live. */
export interface DragFrame {
  plan: Plan;
  guides: [Pt, Pt][];
  snapLine: [Pt, Pt] | null;
  /** The live dimension label. */
  label: { at: Pt; text: string } | null;
  /** A drag-out room's outline. */
  outline: Pt[] | null;
  sel: Sel | null;
}

export type DragEnd =
  | { kind: 'none'; message?: string }
  | { kind: 'plan'; before: Plan; working: Plan; sel: Sel | null; message?: string }
  | { kind: 'dragout'; rid: string; vid: number; depthFt: number };

type Drag =
  | { kind: 'room'; before: Plan; working: Plan; d: RoomDrag; w0: Pt }
  | { kind: 'wall'; before: Plan; working: Plan; d: WallDrag; w0: Pt; snapped: boolean; jamb?: string; off: number }
  | { kind: 'shared'; before: Plan; working: Plan; rid: string; vid: number; n: Pt; w0: Pt; refused: string | null; jamb?: string; off: number }
  | { kind: 'open'; before: Plan; working: Plan; d: OpeningDrag }
  | { kind: 'dragout'; before: Plan; rid: string; vid: number; depthFt: number | null; outline: Pt[] | null };

export const SELECTED_WALL_BIAS_FT = 0.08;

/** A shared opening is one object: either side's hit selects the copy that stands for it (J14). */
export function openSel(plan: Plan, rid: string, oid: number): Sel {
  const c = resolveOpening(plan, rid, oid) ?? { rid, oid };
  return { type: 'open', rid: c.rid, oid: c.oid };
}

/** The hit under a plan point (no gap marks), a 0 in missing wall read as its wall (K5). */
export function hitAt(plan: Plan, wp: Pt, tolFt: number, sel: Sel | null): Exclude<Hit, { type: 'mark' }> | null {
  const h = hitTest(plan, wp, { tolFt, selectedRoomId: sel?.rid ?? null, selectedBiasFt: SELECTED_WALL_BIAS_FT });
  if (!h || h.type === 'mark') return null;
  if (h.type === 'open') {
    const mw = missingAsWall(plan, h.rid, h.oid);
    if (mw) return { type: 'wall', rid: mw.rid, vid: mw.vid, off: mw.off, d: h.d };
  }
  return h;
}

/** The selection a hit makes. */
export function selOf(plan: Plan, h: Exclude<Hit, { type: 'mark' }> | null): Sel | null {
  if (!h) return null;
  if (h.type === 'open') return openSel(plan, h.rid, h.oid);
  if (h.type === 'wall') return { type: 'wall', rid: h.rid, vid: h.vid, off: h.off };
  return { type: 'room', rid: h.rid };
}

/** The handle drawn on a selected wall (its midpoint) or opening (its middle), plan feet. */
export function handlePt(plan: Plan, sel: Sel | null): Pt | null {
  if (!sel || sel.type === 'room') return null;
  const r = findRoom(plan, sel.rid);
  if (!r) return null;
  const vid = sel.type === 'wall' ? sel.vid : r.openings.find((o) => o.id === sel.oid)?.edgeStart;
  const E = vid != null ? edgeOf(r, vid) : null;
  if (!E) return null;
  const L = dist(E.a, E.b);
  const at = sel.type === 'wall' ? L / 2 : (() => {
    const o = r.openings.find((q) => q.id === sel.oid)!;
    return o.offFt + o.widthFt / 2;
  })();
  return { x: E.a.x + ((E.b.x - E.a.x) * at) / (L || 1), y: E.a.y + ((E.b.y - E.a.y) * at) / (L || 1) };
}

/**
 * What a press at `wp` grabs, or null (the viewport pans). A press on the selected item's
 * handle wins first, then the selected item (Ctrl on a wall: pull out a new room).
 */
export function grabAt(plan: Plan, sel: Sel | null, wp: Pt, o: PressInfo): Grab | null {
  const hp = handlePt(plan, sel);
  if (sel && hp && dist(hp, wp) <= o.tolFt * 1.5) {
    if (sel.type === 'wall') return o.ctrl ? { kind: 'dragout', rid: sel.rid, vid: sel.vid } : { kind: 'wall', rid: sel.rid, vid: sel.vid, off: sel.off };
    if (sel.type === 'open') return { kind: 'open', rid: sel.rid, oid: sel.oid };
  }
  const h = hitAt(plan, wp, o.tolFt, sel);
  if (!h) return null;
  if (h.type === 'wall' && o.ctrl) return { kind: 'dragout', rid: h.rid, vid: h.vid };
  // Only the selected thing drags (PRP-WEB-2 W2.1, the phone's rule): a room, and once a room
  // is selected its walls (either side of a shared one) and its doors and windows. Anything
  // else pans, so a drag across an unselected plan never moves a wall by accident.
  if (!sameThing(plan, sel, h)) return null;
  if (h.type === 'open') return { kind: 'open', rid: h.rid, oid: h.oid };
  if (h.type === 'wall') return { kind: 'wall', rid: h.rid, vid: h.vid, off: h.off };
  return { kind: 'room', rid: h.rid };
}

/** The hit is the selected thing (a wall from either side of it; a room by any part of it). */
function sameThing(plan: Plan, sel: Sel | null, h: Exclude<Hit, { type: 'mark' }>): boolean {
  if (!sel) return false;
  if (h.type === 'room') return sel.rid === h.rid;
  if (h.type === 'open') {
    const s = openSel(plan, h.rid, h.oid) as Extract<Sel, { type: 'open' }>;
    if (sel.type === 'open') return sel.rid === s.rid && sel.oid === s.oid;
    return sel.rid === h.rid || sel.rid === s.rid;
  }
  if (sel.rid === h.rid) return true;
  return (sharedMap(plan).get(h.rid)?.get(h.vid) ?? []).some((iv) => iv.orid === sel.rid);
}

const ft = (v: number) => fmtFt(Math.abs(v));

export class EditController {
  private drag: Drag | null = null;

  get active(): boolean { return !!this.drag; }
  get kind(): Drag['kind'] | null { return this.drag?.kind ?? null; }

  /**
   * Start dragging `g` (the press was at `w0`). `editable(rid)` is null when the room may be
   * changed, else why not (DD7). Returns the first frame, or a refusal message (no drag).
   */
  start(plan: Plan, g: Grab, w0: Pt, editable: (rid: string) => string | null): DragFrame | { message: string } {
    const why = editable(g.rid);
    if (why) return { message: why };
    if (g.kind === 'dragout') {
      this.drag = { kind: 'dragout', before: plan, rid: g.rid, vid: g.vid, depthFt: null, outline: null };
      return this.frame({ type: 'wall', rid: g.rid, vid: g.vid, off: 0 });
    }
    if (g.kind === 'open') {
      const working = clone(plan);
      const res = beginOpeningDrag(working, g.rid, g.oid, w0);
      if (!res.ok) return { message: res.message };
      this.drag = { kind: 'open', before: plan, working, d: res.drag };
      return this.frame(openSel(plan, g.rid, g.oid));
    }
    if (g.kind === 'room') {
      const working = clone(plan);
      const res = beginRoomDrag(working, g.rid);
      if (!res.ok) return { message: res.message };
      this.drag = { kind: 'room', before: plan, working, d: res.drag, w0 };
      return this.frame({ type: 'room', rid: g.rid });
    }
    const working = clone(plan);
    const res = beginWallDrag(working, g.rid, g.vid, { allowMeasured: true }); // K8: measured walls drag
    const ivs = sharedMap(plan).get(g.rid)?.get(g.vid) ?? [];
    if (!res.ok && res.err === 'shared' && ivs.length) {
      const r = findRoom(plan, g.rid)!, E = edgeOf(r, g.vid)!;
      this.drag = { kind: 'shared', before: plan, working: plan, rid: g.rid, vid: g.vid, n: outN(r, E.a, E.b), w0, refused: null, off: g.off };
      return this.frame({ type: 'wall', rid: g.rid, vid: g.vid, off: g.off });
    }
    if (!res.ok) {
      return { message: res.message }; // DD3: a diagonal wall is refused ("Only straight walls can be dragged")
    }
    this.drag = { kind: 'wall', before: plan, working, d: res.drag, w0, snapped: false, off: g.off };
    return this.frame({ type: 'wall', rid: g.rid, vid: res.vid, off: g.off });
  }

  /** The pointer is at `wp`. `alt`: no snapping this move. */
  move(wp: Pt, o: { alt?: boolean; pxPerFt: number }): DragFrame | null {
    const D = this.drag;
    if (!D) return null;
    const delta = D.kind === 'dragout' || D.kind === 'open' ? { x: 0, y: 0 } : { x: wp.x - D.w0.x, y: wp.y - D.w0.y };
    if (D.kind === 'dragout') {
      const r = findRoom(D.before, D.rid);
      const E = r && edgeOf(r, D.vid);
      if (!r || !E) return this.frame(null);
      const n = outN(r, E.a, E.b);
      const depth = (wp.x - E.a.x) * n.x + (wp.y - E.a.y) * n.y - wallFtOf(r, D.vid);
      const pv = dragOutPreview(D.before, D.rid, D.vid, depth);
      D.depthFt = pv?.depthFt ?? null;
      D.outline = pv?.outline ?? null;
      return this.frame({ type: 'wall', rid: D.rid, vid: D.vid, off: 0 });
    }
    if (D.kind === 'shared') {
      const working = clone(D.before);
      const res = dragSharedWall(working, D.rid, D.vid, delta.x * D.n.x + delta.y * D.n.y);
      if (res.ok) { D.working = working; D.refused = null; D.jamb = res.jamb; }
      else if (res.err !== 'no-move') D.refused = res.message;
      else D.working = D.before;
      return this.frame({ type: 'wall', rid: D.rid, vid: D.vid, off: D.off });
    }
    if (D.kind === 'open') {
      dragOpening(D.working, D.d, wp);
      return this.frame(openSel(D.working, D.d.rid, D.d.oid));
    }
    if (D.kind === 'wall') {
      if (o.alt) {
        dragWall(D.working, D.d, delta);
        D.snapped = false;
        return this.frame({ type: 'wall', rid: D.d.rid, vid: D.d.vid, off: D.off });
      }
      const pv = dragSegmentPreview(D.working, D.d, delta);
      D.snapped = pv.snapped;
      if (pv.jamb) D.jamb = pv.jamb;
      return this.frame({ type: 'wall', rid: D.d.rid, vid: D.d.vid, off: D.off }, [], pv.line ?? null);
    }
    if (o.alt) {
      const r = findRoom(D.working, D.d.rid);
      const dx = snapIn(delta.x), dy = snapIn(delta.y);
      r?.corners.forEach((p, k) => { p.x = D.d.start[k].x + dx; p.y = D.d.start[k].y + dy; });
      return this.frame({ type: 'room', rid: D.d.rid });
    }
    const { guides } = dragRoom(D.working, D.d, delta, o.pxPerFt);
    return this.frame({ type: 'room', rid: D.d.rid }, guides);
  }

  /** Release: the plan to commit as one undo step (or nothing, or a drag-out to finish). */
  end(): DragEnd {
    const D = this.drag;
    this.drag = null;
    if (!D) return { kind: 'none' };
    if (D.kind === 'dragout') {
      return D.depthFt != null ? { kind: 'dragout', rid: D.rid, vid: D.vid, depthFt: D.depthFt } : { kind: 'none', message: 'Drag farther out to make a room.' };
    }
    // The release cleanup is the core's (W1.1 finish*Drag, the phone's dragEnd): segment readings
    // whose T moved go, damage follows its wall, the no-change check. Read-only rooms are the
    // page's check (runEdit), so none are passed here.
    let fin: DragFinish;
    let sel: Sel | null;
    if (D.kind === 'wall') {
      fin = finishWallDrag(D.before, D.working, D.d, D.snapped);
      sel = { type: 'wall', rid: D.d.rid, vid: fin.merged.get(D.d.vid) ?? D.d.vid, off: D.off };
    } else if (D.kind === 'shared') {
      fin = finishWallDrag(D.before, D.working, null, false);
      sel = { type: 'wall', rid: D.rid, vid: D.vid, off: D.off };
    } else if (D.kind === 'room') {
      fin = finishRoomDrag(D.before, D.working);
      sel = { type: 'room', rid: D.d.rid };
    } else {
      fin = finishOpeningDrag(D.before, D.working);
      sel = openSel(D.working, D.d.rid, D.d.oid);
    }
    const refused = D.kind === 'shared' ? D.refused : null;
    const jamb = D.kind === 'wall' || D.kind === 'shared' ? D.jamb : undefined;
    if (fin.kind !== 'changed') return { kind: 'none', message: refused ?? jamb };
    const notes = [
      fin.breakRemoved ? 'Break removed.' : null,
      refused ?? jamb,
      fin.pruned.length ? `${fin.pruned.map((x) => ft(x.valueFt)).join(', ')} segment measurement${fin.pruned.length > 1 ? 's' : ''} dropped: the wall that met there moved.` : null,
      fin.droppedDamage.length ? droppedText(fin.droppedDamage) : null,
      fin.rough.length ? `${fin.rough.length} measurement${fin.rough.length > 1 ? 's' : ''} went back to rough.` : null,
    ].filter(Boolean) as string[];
    return { kind: 'plan', before: D.before, working: D.working, sel, message: notes.length ? notes.join(' ') : undefined };
  }

  /** Esc: drop the drag; the plan is as it was before it. */
  cancel(): Plan | null {
    const D = this.drag;
    this.drag = null;
    return D ? D.before : null;
  }

  private frame(sel: Sel | null, guides: [Pt, Pt][] = [], snapLine: [Pt, Pt] | null = null): DragFrame {
    const D = this.drag!;
    const plan = D.kind === 'dragout' ? D.before : D.working;
    return { plan, guides, snapLine, label: liveLabel(D), outline: D.kind === 'dragout' ? D.outline : null, sel };
  }
}

/** The live dimension while dragging: the room's size across the wall, the door's distance from its corner, the move. */
function liveLabel(D: Drag): { at: Pt; text: string } | null {
  if (D.kind === 'dragout') {
    if (D.depthFt == null || !D.outline) return null;
    const c = D.outline.reduce((s, p) => ({ x: s.x + p.x / 4, y: s.y + p.y / 4 }), { x: 0, y: 0 });
    return { at: c, text: `${ft(D.depthFt)} deep` };
  }
  if (D.kind === 'room') {
    const r = findRoom(D.working, D.d.rid);
    if (!r) return null;
    const dx = r.corners[0].x - D.d.start[0].x, dy = r.corners[0].y - D.d.start[0].y;
    const [x0, y0, x1, y1] = bbox(r.corners);
    const parts = [Math.abs(dx) >= 1 / 24 ? `${dx > 0 ? '→' : '←'} ${ft(dx)}` : '', Math.abs(dy) >= 1 / 24 ? `${dy > 0 ? '↓' : '↑'} ${ft(dy)}` : ''].filter(Boolean);
    return { at: { x: (x0 + x1) / 2, y: y0 - Math.max(0.6, (y1 - y0) * 0.08) }, text: parts.length ? `Moved ${parts.join('  ')}` : 'Move' };
  }
  if (D.kind === 'open') {
    const r = findRoom(D.working, D.d.rid);
    const o = r?.openings.find((q) => q.id === D.d.oid);
    const E = r && o ? edgeOf(r, o.edgeStart) : null;
    if (!r || !o || !E) return null;
    const L = dist(E.a, E.b) || 1;
    const t = (o.offFt + o.widthFt / 2) / L;
    const n = outN(r, E.a, E.b);
    const at = { x: E.a.x + (E.b.x - E.a.x) * t - n.x * 1.2, y: E.a.y + (E.b.y - E.a.y) * t - n.y * 1.2 };
    return { at, text: `${ft(o.offFt)} from corner · ${ft(L - o.offFt - o.widthFt)} to the other` };
  }
  const rid = D.kind === 'wall' ? D.d.rid : D.rid;
  const vid = D.kind === 'wall' ? D.d.vid : D.vid;
  const r = findRoom(D.working, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return null;
  const [x0, y0, x1, y1] = bbox(r.corners);
  const horiz = Math.abs(E.a.y - E.b.y) < 1e-6;
  const size = horiz ? y1 - y0 : x1 - x0;
  const n = outN(r, E.a, E.b);
  const at = { x: (E.a.x + E.b.x) / 2 + n.x * 1.4, y: (E.a.y + E.b.y) / 2 + n.y * 1.4 };
  return { at, text: `${r.name}: ${ft(size)}` };
}
